import { defineComponent as _defineComponent } from 'vue'
import { type PropType, computed, toRefs, unref, useContext } from "@nuxtjs/composition-api";

import { useVuetify } from "@/composables";
import { type LtkItemHeightCalculation, heroImageOnly } from "@/utils/services/LtkItemHeightCalculator";


export default /*#__PURE__*/_defineComponent({
  __name: 'LtkList',
  props: {
  /**
   * Array of ltk id strings, and the relative order they should be rendered
   *
   * @required
   */
  ltkIds: {
    type: Array as PropType<string[]>,
    required: true,
    default: () => [],
  },
  /**
   * Alias of $fetchState from the providing page
   */
  fetchState: {
    type: Object as PropType<Vue["$fetchState"]>,
    required: true,
  },

  /**
   * Number of items from the end of the shortest column a pagination trigger willbe positioned.
   * When set to -1, this will disable placement of the pagination item.
   *
   * @default -1
   */
  itemsBeforePage: {
    type: Number,
    default: -1,
  },
  /**
   * Spacing in pixels between list items on the vertical axis (virtual only)
   */
  itemVerticalSpacing: {
    type: Number,
    default: 0,
  },
  /**
   * Spacing in pixels between list items on the horizontal axis (virtual only)
   */
  itemHorizontalSpacing: {
    type: Number,
    default: 0,
  },
  /**
   * Number of masonry columns to render based on xs breakpoint.
   */
  xsColumns: {
    type: Number,
    default: 2,
  },
  /**
   * Number of masonry columns to render based on sm breakpoint.
   */
  smColumns: {
    type: Number,
    default: 3,
  },
  /**
   * Number of masonry columns to render based on md breakpoint.
   */
  mdColumns: {
    type: Number,
    default: 3,
  },
  /**
   * Number of masonry columns to render based on lg breakpoint.
   */
  lgColumns: {
    type: Number,
    default: 4,
  },
  /**
   * Number of masonry columns to render based on xl breakpoint.
   */
  xlColumns: {
    type: Number,
    default: 4,
  },
  /**
   * Sizing function to be run on each item in the masonry layout to determine responsive height.
   * You should update this when the ltk item contains more than the hero image, like an avatar,
   * or products.
   *
   * @default (1 / aspectRatio * width)
   */
  itemHeightFunction: {
    type: Function as PropType<LtkItemHeightCalculation>,
    default: heroImageOnly,
  },
  isVisible: {
    type: Boolean,
    default: false,
  },
  quietIntersect: {
    type: Boolean,
    default: true,
  },
},
  emits: ["page", "retry"],
  setup(__props, { emit: emits }: { emit: ({
  (e: "page"): void;
  (e: "retry"): void;
}), expose: any, slots: any, attrs: any }) {

const prps = __props;



const { ltkIds, fetchState, xsColumns, smColumns, lgColumns, xlColumns, mdColumns, itemsBeforePage, quietIntersect } =
  toRefs(prps);

const vuetify = useVuetify();
const ctx = useContext();



/**
 * Number of masonry columns to be rendered with the current screensize. On SSR, use helper to guess
 */
const columnCount = computed(() => {
  // Guess at screen width
  if (process.server) {
    return ctx.isMobile ? xsColumns : ctx.isTablet ? smColumns : lgColumns;
  }

  return vuetify.breakpoint.xl
    ? xlColumns
    : vuetify.breakpoint.lg
    ? lgColumns
    : vuetify.breakpoint.md
    ? mdColumns
    : vuetify.breakpoint.sm
    ? smColumns
    : xsColumns;
});

/**
 * Checks if the pagination elements should be shown or not.
 */
const showPagination = computed(() => {
  return itemsBeforePage.value !== -1 && fetchState.value.pending === false;
});

/**
 * Handler for pagination intersection elements
 */
const handlePagination = ([entry]: IntersectionObserverEntry[]) => {
  if (entry.isIntersecting) {
    emits("page");
  }
};

const handleRetry = () => {
  if (quietIntersect.value) {
    emits("page");
  } else {
    emits("retry");
  }
};

return { __sfc: true,prps, ltkIds, fetchState, xsColumns, smColumns, lgColumns, xlColumns, mdColumns, itemsBeforePage, quietIntersect, vuetify, ctx, emits, columnCount, showPagination, handlePagination, handleRetry, unref }
}

})